import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useCartStore } from './store/cartStore'
import GuestLayout from './layouts/GuestLayout'
import UserLayout from './layouts/UserLayout'
import AdminLayout from './layouts/AdminLayout'
import ProtectedRoute from './components/ProtectedRoute'
import EnterpriseRouteGuard from './components/EnterpriseRouteGuard'
import InstallPWAButton from './components/InstallPWAButton'
import { initGlobalImageFix, fixExistingImages } from './utils/globalImageFix'

// Pages
import HomePage from './pages/HomePage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import VerifyEmailPage from './pages/auth/VerifyEmailPage'
import VerificationRequiredPage from './pages/auth/VerificationRequiredPage'
import ProductDetailPage from './pages/ProductDetailPage'
import SearchPage from './pages/SearchPage'
import AuctionsPage from './pages/AuctionsPage'
import ProductsPage from './pages/ProductsPage'
import CategoriesPage from './pages/CategoriesPage'
import Enterprise from './pages/Enterprise'
import VendorDetailPage from './pages/VendorDetailPage'
import ROICalculator from './pages/ROICalculator'
import ProfilePage from './pages/ProfilePage'
import CreateProductPage from './pages/CreateProductPage'
import CheckoutPage from './pages/CheckoutPage'
import OrderSuccessPage from './pages/OrderSuccessPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import DashboardPage from './pages/user/DashboardPage'
import MessagesPage from './pages/user/MessagesPage'
import MyProductsPage from './pages/user/MyProductsPage'
import VendorProfileManagementPage from './pages/user/VendorProfileManagementPage'
import WalletPage from './pages/WalletPage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import CreateCategoryPage from './pages/admin/CreateCategoryPage'
import GovVendorsPage from './pages/admin/GovVendorsPage'
import { AdminSettingsPage } from './modules/admin-settings-ui'
import TestAutoLogoutPage from './pages/TestAutoLogoutPage'
import BuyerQuoteDashboard from './pages/BuyerQuoteDashboard'
import VendorQuoteDashboard from './pages/VendorQuoteDashboard'
import QuoteDetailPage from './pages/QuoteDetailPage'
import NotFoundPage from './pages/NotFoundPage'
import CompanyProfilePage from './pages/CompanyProfilePage'
import ComingSoonPage from './pages/ComingSoonPage'
import GovRfqsPage from './pages/enterprise/gov-rfqs'
import GovRegisterPage from './pages/gov/GovRegisterPage'
import GovRegisterSuccessPage from './pages/gov/GovRegisterSuccessPage'
import GovAgenciesPage from './pages/admin/GovAgenciesPage'
import RfqRespondPage from './pages/gov/RfqRespondPage'
import GovDashboard from './pages/gov/GovDashboard'
import GovSettings from './pages/gov/GovSettings'
import { CreateGovRfqPage } from './pages/gov/CreateGovRfqPage'
import GovVendorDetailPage from './pages/gov/GovVendorDetailPage'
import GovVendorsListingPage from './pages/gov/GovVendorsPage'
import RfqRouter from './components/gov/RfqRouter'
import ProposalRouter from './components/gov/ProposalRouter'
import CompliancePage from './pages/gov/CompliancePage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import { EnterpriseRfqPage } from './pages/enterprise/EnterpriseRfqPage'
import { VendorRfqPage } from './pages/enterprise/VendorRfqPage'
import { RfqDetailPage } from './pages/enterprise/RfqDetailPage'
import { VendorResponsesPage } from './pages/enterprise/VendorResponsesPage'
import PostQuoteActivityPage from './pages/enterprise/PostQuoteActivityPage'
import PurchaseOrdersPage from './pages/enterprise/PurchaseOrdersPage'
import PurchaseOrderDetailPage from './pages/enterprise/PurchaseOrderDetailPage'
import PurchaseOrderEditPage from './pages/enterprise/PurchaseOrderEditPage'
import DigitalAgreementsPage from './pages/enterprise/DigitalAgreementsPage'
import FulfillmentPage from './pages/enterprise/FulfillmentPage'
import InvoicingPage from './pages/enterprise/InvoicingPage'
import VendorFeedbackPage from './pages/enterprise/VendorFeedbackPage'
import ReportsPage from './pages/enterprise/ReportsPage'
import EnterpriseDashboardPage from './pages/enterprise/EnterpriseDashboardPage'
import { GovDashboardPage } from './pages/gov/GovDashboardPage'

// Theme Provider
import { ThemeProvider } from './components/ThemeProvider'
import ComingSoonGate from './components/ComingSoonGate'

function App() {
  const { user, checkAuth } = useAuthStore()
  const { setCurrentUser } = useCartStore()

  useEffect(() => {
    checkAuth()
    
    // Initialize global image fix to handle problematic URLs
    initGlobalImageFix()
    
    // Fix any existing images on the page
    setTimeout(() => {
      fixExistingImages()
    }, 1000)
  }, [checkAuth])

  // Initialize cart with current user on app load
  useEffect(() => {
    const userId = user?.id || null
    setCurrentUser(userId)
  }, [user?.id, setCurrentUser])

  return (
    <ThemeProvider>
      <ComingSoonGate>
        <div className="App min-h-screen bg-gray-50 dark:bg-gray-900">
          <InstallPWAButton />
          <Routes>
        {/* Public routes */}
        <Route path="/" element={<GuestLayout />}>
          <Route index element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/auctions" element={<AuctionsPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/enterprise/vendor/:vendorId" element={<VendorDetailPage />} />
          <Route path="/gov/register" element={<GovRegisterPage />} />
          <Route path="/gov/register-success" element={<GovRegisterSuccessPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </Route>

        {/* Auth routes - redirect to dashboard if already logged in */}
        <Route path="/" element={<GuestLayout />}>
          <Route 
            path="/login" 
            element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <ProtectedRoute requireAuth={false}>
                <RegisterPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/verification-required" element={<VerificationRequiredPage />} />
        </Route>

        {/* Protected routes that require authentication but can be accessed by guests with redirect */}
        <Route path="/" element={<GuestLayout />}>
          <Route 
            path="/checkout" 
            element={
              <ProtectedRoute requireAuth={true}>
                <CheckoutPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/order-success" 
            element={
              <ProtectedRoute requireAuth={true}>
                <OrderSuccessPage />
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* Protected user routes */}
        <Route path="/" element={<UserLayout />}>
          <Route path="/enterprise" element={<Enterprise />} />
          <Route path="/enterprise/dashboard" element={
            <ProtectedRoute requireAuth={true}>
              <EnterpriseDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/gov-rfqs" element={<GovRfqsPage />} />
          
          {/* Enterprise RFQ Routes */}
          <Route path="/enterprise/rfqs" element={
            <ProtectedRoute requireAuth={true}>
              <EnterpriseRfqPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/rfqs/:rfqId" element={
            <ProtectedRoute requireAuth={true}>
              <RfqDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/vendor-rfqs" element={
            <ProtectedRoute requireAuth={true}>
              <VendorRfqPage />
            </ProtectedRoute>
          } />
          <Route path="/quotes/vendor" element={
            <ProtectedRoute requireAuth={true}>
              <VendorResponsesPage />
            </ProtectedRoute>
          } />
          
          {/* Post-Quote Activity Manager Routes */}
          <Route path="/enterprise/post-quote-activity" element={
            <ProtectedRoute requireAuth={true}>
              <PostQuoteActivityPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/purchase-orders" element={
            <ProtectedRoute requireAuth={true}>
              <PurchaseOrdersPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/purchase-orders/:id" element={
            <ProtectedRoute requireAuth={true}>
              <PurchaseOrderDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/purchase-orders/:id/edit" element={
            <ProtectedRoute requireAuth={true}>
              <PurchaseOrderEditPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/agreements" element={
            <ProtectedRoute requireAuth={true}>
              <DigitalAgreementsPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/fulfillments" element={
            <ProtectedRoute requireAuth={true}>
              <FulfillmentPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/invoices" element={
            <ProtectedRoute requireAuth={true}>
              <InvoicingPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/feedback" element={
            <ProtectedRoute requireAuth={true}>
              <VendorFeedbackPage />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/post-quote-activity/reports" element={
            <ProtectedRoute requireAuth={true}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          
          {/* Government Agency Routes - MODULE 6 */}
          <Route path="/gov/dashboard" element={
            <ProtectedRoute requireAuth={true}>
              <GovDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/government" element={
            <ProtectedRoute requireAuth={true}>
              <GovDashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/settings" element={
            <ProtectedRoute requireAuth={true}>
              <GovSettings />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/rfqs/create" element={
            <ProtectedRoute requireAuth={true}>
              <CreateGovRfqPage />
            </ProtectedRoute>
          } />
          
          {/* Government RFQ Response Routes - MODULE 6 */}
          <Route path="/gov/rfqs" element={
            <ProtectedRoute requireAuth={true}>
              <RfqRouter />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/rfqs/:id/respond" element={
            <ProtectedRoute requireAuth={true}>
              <RfqRespondPage />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/vendors" element={
            <ProtectedRoute requireAuth={true}>
              <GovVendorsListingPage />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/vendor/:vendorId" element={
            <ProtectedRoute requireAuth={true}>
              <GovVendorDetailPage />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/proposals" element={
            <ProtectedRoute requireAuth={true}>
              <ProposalRouter />
            </ProtectedRoute>
          } />
          
          <Route path="/gov/compliance" element={
            <ProtectedRoute requireAuth={true}>
              <CompliancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/roi-calculator" element={<ROICalculator />} />
          <Route path="/coming-soon" element={<ComingSoonPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/sell" element={<CreateProductPage />} />
          <Route path="/sell/edit/:id" element={<CreateProductPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute requireAuth={true}>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <EnterpriseRouteGuard requiredPermission="canViewMyOrders">
              <OrdersPage />
            </EnterpriseRouteGuard>
          } />
          <Route path="/order/:id" element={<OrderDetailPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/products/my" element={
            <EnterpriseRouteGuard requiredPermission="canViewMyListings">
              <MyProductsPage />
            </EnterpriseRouteGuard>
          } />
          <Route path="/wallet" element={
            <EnterpriseRouteGuard requiredPermission="canViewWallet">
              <WalletPage />
            </EnterpriseRouteGuard>
          } />
          <Route path="/quotes" element={
            <EnterpriseRouteGuard requiredPermission="canViewQuotes">
              <BuyerQuoteDashboard />
            </EnterpriseRouteGuard>
          } />
          <Route path="/quotes/vendor" element={
            <EnterpriseRouteGuard requiredPermission="canViewQuotes">
              <VendorQuoteDashboard />
            </EnterpriseRouteGuard>
          } />
          <Route path="/quote/:id" element={<QuoteDetailPage />} />
          <Route path="/vendor-profile" element={<VendorProfileManagementPage />} />
          <Route path="/company-profile" element={<CompanyProfilePage />} />
          <Route path="/company/profile/manage" element={<VendorProfileManagementPage />} />
          <Route path="/test-auto-logout" element={<TestAutoLogoutPage />} />
        </Route>

        {/* Admin routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="categories/create" element={<CreateCategoryPage />} />
          <Route path="gov-vendors" element={<GovVendorsPage />} />
          <Route path="gov-agencies" element={<GovAgenciesPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>

        {/* 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
        </div>
      </ComingSoonGate>
    </ThemeProvider>
  )
}

export default App 